import { useFormik } from "formik";
import { find, get, isEmpty } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import {
  MdOutlineCake,
  MdOutlineDiversity1,
  MdOutlineFace,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  RELATIONSHIP,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";

import { Col, Row } from "antd";
import { BiIdCard } from "react-icons/bi";
import { RiCalendarEventLine } from "react-icons/ri";
import ActionButtons from "../Components/ActionButtons";
import Cost from "../Components/Cost";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DocumentDownload from "./DocumentDownload";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "Premier",
  2: "Deuxième",
  3: "Troisième",
  4: "Quatrième",
  5: "Cinquième",
  6: "Sixième",
  7: "Septième",
  8: "Huitième",
  9: "Neuvième",
  10: "Dixième",
  11: "Onzième",
  12: "Douzième",
};

function View({
  subscription,
  onSubmit,
  generalCost,
  updateSponsorship,
  updateProjectStatus,
  attachments,
  downloadFile,
  documentsLoading,
  onBack,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const { t: trans } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = trans(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    },
  );
  const [origin, setOrigin] = useState(
    get(subscription, "project.contact.fields.membershipOrigin", null),
  );
  const totalCost = useMemo(
    () =>
      get(
        subscription,
        `project.fields.upsells.${get(subscription, "insurancePlan.id")}`,
        [],
      )
        ?.reduce((acc, { cost }) => (acc += cost), get(generalCost, "cost", 0))
        ?.toFixed(2),
    [],
  );

  const EDIT_BLOCK = [
    {
      icon: <MdOutlineFace size={24} />,
      label: "Vous êtes",
      value: `${get(subscription, "project.contact.user.firstname")} ${get(
        subscription,
        "project.contact.user.lastname",
      )}`,
      template: TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_AMELLIS].ADHERENT_INFO,
      isBeforeLocked: true,
    },
    {
      icon: <MdOutlineCake size={24} />,
      label: "Date de naissance",
      value: `${moment(
        get(subscription, "project.contact.user.birthDate"),
      ).format(DATE_FORMAT)}`,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_AMELLIS].ADHERENT_BIRTHDATE,
      isBeforeLocked: true,
    },
    {
      icon: <BiIdCard size={24} />,
      label: "Adresse postale",
      value: (
        <>
          {get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ADDRESS,
            }),
            "value",
          )}
          <br />
          {`${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.CITY,
            }),
            "value",
          )} - ${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ZIP_CODE,
            }),
            "value",
          )}`}
        </>
      ),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_AMELLIS].ADHERENT_TELECOMS,
    },
    ...(!isEmpty(get(subscription, "project.contact.relatedPersons", []))
      ? [
          {
            icon: <MdOutlineDiversity1 size={24} />,
            label: "Vos bénéficiaires",
            value: [
              get(subscription, "project.contact.relatedPersons", [])
                .filter(
                  ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
                )
                .map(({ identity }, i) => (
                  <div>
                    {`${get(identity, "user.firstname", "") || ""} ${
                      get(identity, "user.lastname", "") || ""
                    }`}{" "}
                    <span>{"(Votre conjoint)"}</span>
                  </div>
                )),
              get(subscription, "project.contact.relatedPersons", [])
                .filter(
                  ({ relationship }) => relationship === RELATIONSHIP.CHILD,
                )
                .map(({ identity }, i) => (
                  <div>
                    {`${get(identity, "user.firstname", "") || ""} ${
                      get(identity, "user.lastname", "") || ""
                    }`}{" "}
                    <span>{`(Votre ${NUMBERS_MAPPING[i + 1]} enfant)`}</span>
                  </div>
                )),
            ],
            template:
              TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_AMELLIS].RELATED_PERSONS,
          },
        ]
      : []),
  ];

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      additionalInfo: get(subscription, "additionalInfo"),
    },
    onSubmit: onSubmit,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  return (
    <Layout>
      <Header
        title="Nous y sommes ! Votre adhésion est prête pour être signée"
        highlight="Votre adhésion est prête"
        subtitle="Souscription : Récapitulatif"
      />

      <div className="recap__wrapper">
        <Row justify={"center"} gutter={[24, 24]}>
          <Col xs={24} sm={20} md={18} lg={12} xl={10}>
            <div className="recap__wrapper--col border">
              <div className="recap__wrapper--title">Offre</div>
              <div className="recap__wrapper--card">
                <h3>{get(subscription, "insurancePlan.name")}</h3>
                {get(generalCost, "cost", 0) ? (
                  <Cost
                    cost={totalCost}
                    currency={get(generalCost, "currency")}
                  />
                ) : (
                  <span>{get(subscription, "insurancePlan.alias")}</span>
                )}
              </div>

              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">
                  Informations de l’adhérent
                </div>
                {EDIT_BLOCK.map(
                  ({
                    icon,
                    label,
                    value,
                    template,
                    isBeforeLocked = false,
                  }) => (
                    <div className="recap__wrapper--edit" key={label}>
                      {icon}
                      <div className="recap__wrapper--edit--text">
                        <span>{label}</span>
                        <div className="recap__wrapper--edit--text--value">
                          {value}
                        </div>
                      </div>
                      <div className="recap__wrapper--edit--icon">
                        <FiEdit
                          onClick={() =>
                            updateProjectStatus({ template, isBeforeLocked })
                          }
                          size={20}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={20} md={18} lg={12} xl={10}>
            <div className="recap__wrapper--col">
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">Date d’effet </div>
                <div className="recap__wrapper--edit">
                  <RiCalendarEventLine size={24} />
                  <div className="recap__wrapper--edit--text">
                    <span>Date d’effet</span>
                    <div className="recap__wrapper--edit--text--value">
                      {moment(
                        get(subscription, "project.contract.issuanceDate"),
                      ).format(DATE_FORMAT)}
                    </div>
                  </div>
                  <div className="recap__wrapper--edit--icon">
                    <FiEdit
                      onClick={() =>
                        updateProjectStatus({
                          template:
                            TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_AMELLIS]
                              .EFFECTIVE_DATE,
                        })
                      }
                      size={20}
                    />
                  </div>
                </div>
              </div>
              <div className="recap__wrapper--blocks">
                <div className="recap__wrapper--title">
                  Documents contractuels
                </div>

                <DocumentDownload
                  items={attachments}
                  download={(id) => downloadFile(id)}
                />
              </div>
              <ActionButtons
                onBack={onBack}
                // onNext={() => onSubmit({ origin })}
                // disabled={!origin || documentsLoading}
                onNext={handleSubmit}
                disabled={!isEmpty(formProps.errors)}
                loading={documentsLoading}
                buttonText={"Je signe mon adhésion"}
              />
              <p className="recap__wrapper--note">
                En cliquant sur le bouton “Je signe mon adhésion”, j'affirme mon
                accord et mon acceptation des Engagements et Consentements liés
                à cette action,
              </p>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default View;
